import { GraphQLFetchPolicy } from '@getaccept/lib-shared-new/src/graphql/graphql-fetch-policy';
import type {
  DealroomFile,
  PresignedLink,
} from '@getaccept/dsr-shared-new/src/files/types/dealroom-file';
import type { LoadFilesPayload } from '@getaccept/dsr-shared-new/src/files/types/load-files-payload';
import type { CreateFileDto } from '@getaccept/dsr-shared-new/src/api/files/types/file-dto';
import { FileSource } from '@getaccept/dsr-shared-new/src/files/types/enums/file-source';
import { FileStatus } from '@getaccept/dsr-shared-new/src/files/types/enums/file-status';
import { apolloClient } from '../../../apollo';
import { FileQueries } from '../graphql/file.queries';
import { FileMutations } from '../graphql/file.mutations';

export class FileService {
  static async load({
    limit,
    offset,
    sortOrder,
    sortBy,
    searchTerms,
    participantIds,
  }: LoadFilesPayload): Promise<{ files: DealroomFile[]; totalCount: number }> {
    const {
      data: {
        listDealroomFiles: { files, totalCount },
      },
    } = await apolloClient.query({
      query: FileQueries.load,
      variables: {
        limit,
        offset: offset * limit,
        sortOrder,
        sortBy,
        searchTerms,
        participantIds,
        statuses: [FileStatus.Uploaded, FileStatus.Uploading],
      },
      fetchPolicy: GraphQLFetchPolicy.NetworkOnly,
    });
    return { files, totalCount };
  }

  static async getDownloadUrl(fileId: string): Promise<string> {
    const {
      data: {
        dealroomFileDownloadLink: { presignedLink },
      },
    } = await apolloClient.query({
      query: FileQueries.getDownloadUrl,
      variables: { fileId },
      fetchPolicy: GraphQLFetchPolicy.NoCache,
    });
    return presignedLink;
  }

  static async getUploadLink(
    fileToUpload: File
  ): Promise<{ fileId: string; presignedLink: PresignedLink }> {
    try {
      const {
        data: {
          createDealroomFile: { presignedLink, file },
        },
      } = await apolloClient.mutate({
        mutation: FileMutations.getUploadUrl,
        variables: {
          payload: { fileName: fileToUpload.name, source: FileSource.Files },
        },
      });
      return { fileId: file.id, presignedLink };
    } catch {
      throw fileToUpload.name;
    }
  }

  static async upload(presignedLink: PresignedLink, file: File): Promise<void> {
    try {
      const body = new FormData();
      presignedLink.headers
        .split(',')
        .map(header => header.split(':'))
        .forEach(([key, value]) => body.append(key, value));
      body.append('file', file);

      await fetch(presignedLink.url, {
        method: 'POST',
        body,
      });
    } catch {
      throw file.name;
    }
  }

  static async update(fileId: string, payload: Partial<CreateFileDto>): Promise<void> {
    await apolloClient.mutate({
      mutation: FileMutations.update,
      variables: { fileId, payload },
    });
  }

  static async delete(fileId: string): Promise<void> {
    await apolloClient.mutate({
      mutation: FileMutations.delete,
      variables: { fileId },
    });
  }
}
